import { supabase } from './supabase';

/**
 * Funil (pipeline) — fonte única de "qual funil é o de venda nova" e da
 * preferência de funil ativo do Kanban.
 *
 * Motivo de existir (ago/2026): board e wizard tinham defaults independentes e
 * ambos caíam em `data[0]` de um `order('created_at')`. Os dois funis de produção
 * nasceram no MESMO insert (2026-01-29 10:51:47.033987), então esse ORDER BY não
 * desempata e o Postgres pode devolver em qualquer ordem. Resultado real: o
 * vendedor criava a negociação no Funil de Vendas enquanto o quadro dele exibia
 * Pós-Venda — e o quadro filtra por pipeline_id sem avisar nada, então a
 * negociação simplesmente não existia para ele.
 */

export const FUNIL_VENDAS = 'Funil de Vendas';
export const FUNIL_POS_VENDA = 'Pós-Venda';

export interface PipelineRef {
    id: string;
    name: string;
}

const normalizar = (s: string) =>
    s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Lista os funis em ordem determinística. O `id` é o desempate que falta em
 *  todo `order('created_at')` solto do repositório. */
export const listarPipelines = async () => {
    const { data, error } = await supabase
        .from('pipelines')
        .select('*')
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });
    if (error) throw error;
    return data || [];
};

/**
 * O funil de VENDA NOVA, resolvido sobre uma lista já carregada.
 * Ordem: nome exato -> nome com "venda" que não seja pós-venda -> primeiro item.
 *
 * O segundo passo existe porque `views/Config/PipelineSettings.tsx` deixa
 * renomear funil livremente. E precisa excluir "pós-venda" explicitamente:
 * /venda/i casa com "Pós-Venda" — era esse o bug da Prospecção.
 */
export const funilDeVendas = <T extends PipelineRef>(lista: T[] | null | undefined): T | undefined => {
    const itens = lista || [];
    return (
        itens.find(p => normalizar(p.name) === normalizar(FUNIL_VENDAS)) ??
        itens.find(p => /venda/i.test(p.name) && !/p[oó]s[\s-]*venda/i.test(p.name)) ??
        itens[0]
    );
};

/** Id do funil de venda nova direto do banco, para quem não tem a lista em mão.
 *  `maybeSingle` de propósito: renomear/duplicar funil em Config não pode
 *  derrubar a tela com PGRST116, como acontece nos `.single()` espalhados. */
export const buscarFunilDeVendasId = async (): Promise<string | null> => {
    const { data } = await supabase
        .from('pipelines')
        .select('id, name')
        .eq('name', FUNIL_VENDAS)
        .limit(1)
        .maybeSingle();
    if (data?.id) return data.id;

    // Nome mudou: cai na mesma heurística da lista completa.
    try {
        return funilDeVendas(await listarPipelines())?.id ?? null;
    } catch {
        return null;
    }
};

// ── Funil ativo do Kanban ───────────────────────────────────────────────────

const CHAVE_LEGADA = 'vigorhub_active_pipeline';

/** Namespaced por usuário — a chave antiga era global e vazava entre contas na
 *  mesma máquina (mesmo padrão de `vigorhub_financeiro_visible_cards_${id}`). */
export const chaveFunilAtivo = (userId: string) => `vigorhub_active_pipeline_${userId}`;

/**
 * Funil ativo salvo. A chave legada é DESCARTADA, nunca adotada: é exatamente o
 * valor que prendeu o vendedor no Pós-Venda, e adotá-la seria atribuir a escolha
 * de um usuário a quem logar primeiro naquele navegador. Custa uma reescolha de
 * funil e auto-cura os aparelhos já afetados.
 */
export const lerFunilAtivo = (userId?: string | null): string | null => {
    try {
        localStorage.removeItem(CHAVE_LEGADA);
        if (!userId) return null;
        return localStorage.getItem(chaveFunilAtivo(userId));
    } catch {
        return null;
    }
};

export const gravarFunilAtivo = (userId: string | null | undefined, pipelineId: string | null | undefined) => {
    if (!userId || !pipelineId) return;
    try {
        localStorage.setItem(chaveFunilAtivo(userId), pipelineId);
    } catch {
        /* modo privado / storage cheio: preferência de UI não vale quebrar o fluxo */
    }
};

/**
 * Avisa o Kanban. Com `pipelineId`, o quadro PASSA A EXIBIR esse funil — é o que
 * impede a negociação recém-criada de nascer fora da vista. Sem detail, é só
 * recarregar (formato antigo, ainda usado por DetailFeed e discountApproval).
 */
export const avisarKanban = (pipelineId?: string | null) => {
    window.dispatchEvent(
        new CustomEvent('refreshKanban', {
            detail: pipelineId ? { pipelineId } : undefined,
        })
    );
};
